import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useNavigate} from 'react-router-dom';
import {FiCheckCircle, FiChevronDown, FiCoffee, FiCreditCard, FiEye, FiFileText, FiMapPin, FiPhone, FiRefreshCw, FiShoppingBag, FiTruck, FiUser, FiXCircle} from 'react-icons/fi';

import {cancelOrder, confirmDeliveryByCustomer, getOrderById, getOrders, getOrderTracking} from '../api/api';

const ORDERS_KEY = 'orders';

const CUSTOMER_STATUS_MAP = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready for delivery',
  ready_for_delivery: 'Ready for delivery',
  rider_assigned: 'Fetching your order',
  picked_up: 'Picked up',
  en_route: 'On the way',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  rejected: 'Rejected',
};

const STATUS_COLORS = {
  pending: {bg: '#F4EBD8', text: '#B85C38'},
  confirmed: {bg: '#E9F4FF', text: '#0F3D2E'},
  preparing: {bg: '#F4EBD8', text: '#B85C38'},
  ready_for_delivery: {bg: '#F4EBD8', text: '#0F3D2E'},
  picked_up: {bg: '#F4EBD8', text: '#B85C38'},
  en_route: {bg: '#E9F7EF', text: '#27AE60'},
  delivered: {bg: '#E9F7EF', text: '#27AE60'},
  completed: {bg: '#E9F7EF', text: '#27AE60'},
  cancelled: {bg: '#FFE9E9', text: '#D9534F'},
  rejected: {bg: '#FFE9E9', text: '#D9534F'},
};

const normalizeStatusKey = status => String(status || 'pending').trim().toLowerCase().replace(/\s+/g, '_');

const normalizeMoney = value => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const cleaned =
    typeof value === 'string' ? value.replace(',', '.').trim() : value;
  const number = Number(cleaned);

  if (Number.isInteger(number) && number >= 1000) {
    return number / 100;
  }

  return Number.isFinite(number) ? number : 0;
};

const formatMoney = value => normalizeMoney(value).toFixed(2);

const formatDateTime = value => {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';

  return `${date.toLocaleDateString()} • ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const titleCase = value => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, character => character.toUpperCase());

const getOrderMode = order =>
  titleCase(order?.orderMode || order?.deliveryMode || order?.orderType || 'Order');

const getRiderDetails = (order, tracking) => {
  const rider = tracking?.rider || order?.rider || order?.assignedRider || order?.driver || null;
  const name = rider?.name || [rider?.firstName, rider?.lastName].filter(Boolean).join(' ') || tracking?.riderName || order?.riderName || '';

  return {
    assigned: Boolean(rider || name),
    name: name || 'No rider assigned yet',
    phone: rider?.phone || tracking?.riderPhone || order?.riderPhone || '',
    vehicleType: rider?.vehicleType || tracking?.vehicleType || order?.vehicleType || '',
    numberPlate: rider?.numberPlate || tracking?.numberPlate || order?.numberPlate || '',
  };
};

const loadStoredOrders = () => {
  try {
    const raw = localStorage.getItem(ORDERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load local orders:', error);
    return [];
  }
};

const saveStoredOrders = orders => {
  try {
    localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  } catch (error) {
    console.error('Failed to save local orders:', error);
  }
};

const getOrderId = order => order?.id || order?._id || order?.orderId;

const getRestaurantName = order => {
  const directRestaurant = typeof order?.restaurant === 'string' ? order.restaurant : order?.restaurant?.name;
  const directOutlet = typeof order?.outlet === 'string' ? order.outlet : order?.outlet?.name;

  return (
    order?.restaurantName ||
    order?.outletName ||
    directRestaurant ||
    directOutlet ||
    order?.restaurantData?.name ||
    order?.outletData?.name ||
    order?.orderItems?.[0]?.menuItem?.restaurant?.name ||
    order?.items?.[0]?.menuItem?.restaurant?.name ||
    'Restaurant'
  );
};

const getOrderItems = order => {
  if (Array.isArray(order?.orderItems)) return order.orderItems;
  if (Array.isArray(order?.items)) return order.items;
  return [];
};

const getOrderItemCount = order => {
  return getOrderItems(order).reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
};

const calculateTotal = order => {
  if (order?.totalPrice || order?.total) return normalizeMoney(order.totalPrice || order.total);

  return getOrderItems(order).reduce((sum, item) => {
    const price = normalizeMoney(item?.priceAtOrder || item?.price || item?.menuItem?.price);
    return sum + price * Number(item?.quantity || 0);
  }, 0);
};

const getOrderTips = order => {
  const preDelivery = normalizeMoney(
    order?.tipAmount ?? order?.preDeliveryTip ?? order?.tip,
  );
  const postDeliveryStatus = String(order?.postDeliveryTipStatus || '').toLowerCase();
  const postDelivery = ['success', 'paid', 'confirmed', 'completed'].includes(postDeliveryStatus)
    ? normalizeMoney(order?.postDeliveryTipAmount)
    : 0;

  return {
    preDelivery,
    postDelivery,
    total: preDelivery + postDelivery,
  };
};

const normalizePaymentStatus = order =>
  String(
    order?.paymentStatus ||
      order?.payment_status ||
      (order?.paymentConfirmed ? 'success' : 'pending'),
  )
    .trim()
    .toLowerCase();

const isCardPaymentOrder = order =>
  ['card', 'debit card', 'credit card'].includes(
    String(order?.paymentMethod || order?.payment_method || '').toLowerCase(),
  );

const isPaidOrder = order =>
  ['success', 'paid', 'confirmed', 'completed'].includes(
    normalizePaymentStatus(order),
  );

const isPaymentPendingOrder = order =>
  isCardPaymentOrder(order) &&
  !isPaidOrder(order) &&
  !['cancelled', 'canceled'].includes(normalizeStatusKey(order?.status));

const getPaymentUrl = order =>
  order?.ikhokhaPaylinkUrl ||
  order?.paymentUrl ||
  order?.paymentLink ||
  order?.paylinkUrl ||
  order?.payLinkUrl ||
  order?.payment?.paylinkUrl ||
  order?.payment?.paymentUrl ||
  order?.payment?.url ||
  '';

const mergeOrders = (localOrders, apiOrders) => {
  const map = new Map();

  [...localOrders, ...apiOrders].forEach(order => {
    const id = getOrderId(order);
    if (!id) return;
    const existing = map.get(String(id)) || {};
    map.set(String(id), {...existing, ...order, id});
  });

  return Array.from(map.values()).sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return dateB - dateA;
  });
};

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(() => loadStoredOrders());
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [visibleCount, setVisibleCount] = useState(6);
  const [confirmingId, setConfirmingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [orderTracking, setOrderTracking] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const selectedOrderId = getOrderId(selectedOrder);

  useEffect(() => {
    if (!selectedOrderId) {
      setOrderTracking(null);
      return undefined;
    }

    let active = true;
    setDetailsLoading(true);
    setOrderTracking(null);

    Promise.allSettled([
      getOrderById(selectedOrderId),
      getOrderTracking(selectedOrderId),
    ]).then(([orderResult, trackingResult]) => {
      if (!active) return;

      if (orderResult.status === 'fulfilled' && orderResult.value) {
        setSelectedOrder(current =>
          String(getOrderId(current)) === String(selectedOrderId)
            ? {...current, ...orderResult.value}
            : current,
        );
      }

      if (trackingResult.status === 'fulfilled') {
        setOrderTracking(trackingResult.value || null);
      }

      setDetailsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [selectedOrderId]);

  const fetchLatestOrders = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token || token === 'null') {
      setOrders([]);
      saveStoredOrders([]);
      setLoading(false);
      return;
    }

    try {
      setErrorMessage('');
      const response = await getOrders();
      const apiOrders = Array.isArray(response) ? response : response?.data || [];
      const databaseOrders = mergeOrders([], Array.isArray(apiOrders) ? apiOrders : []);
      setOrders(databaseOrders);
      saveStoredOrders(databaseOrders);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatestOrders();
    const interval = setInterval(fetchLatestOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchLatestOrders]);

  const visibleOrders = useMemo(() => orders.slice(0, visibleCount), [orders, visibleCount]);

  const handleConfirmDelivery = async order => {
    const orderId = getOrderId(order);
    if (!orderId) return;

    try {
      setConfirmingId(orderId);
      await confirmDeliveryByCustomer(orderId);
      const updatedOrders = orders.map(item =>
        String(getOrderId(item)) === String(orderId)
          ? {...item, status: 'delivered', customerConfirmed: true, updatedAt: new Date().toISOString()}
          : item,
      );
      setOrders(updatedOrders);
      saveStoredOrders(updatedOrders);
      setSelectedOrder(prev => (prev ? {...prev, status: 'delivered', customerConfirmed: true} : prev));
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to confirm delivery.');
    } finally {
      setConfirmingId(null);
    }
  };

  const updateOrderLocally = (orderId, updates) => {
    const updatedOrders = orders.map(item =>
      String(getOrderId(item)) === String(orderId)
        ? {...item, ...updates, updatedAt: new Date().toISOString()}
        : item,
    );
    setOrders(updatedOrders);
    saveStoredOrders(updatedOrders);
    setSelectedOrder(prev =>
      prev && String(getOrderId(prev)) === String(orderId)
        ? {...prev, ...updates, updatedAt: new Date().toISOString()}
        : prev,
    );
  };

  const handleCompletePayment = order => {
    const paymentUrl = getPaymentUrl(order);

    if (!paymentUrl) {
      setErrorMessage(
        'Payment link is unavailable for this order. Please cancel it and place the order again.',
      );
      return;
    }

    localStorage.setItem(
      'pendingPaymentData',
      JSON.stringify({
        order,
        serverOrder: order,
        payment: {paylinkUrl: paymentUrl},
        createdAt: new Date().toISOString(),
      }),
    );

    window.location.assign(paymentUrl);
  };

  const handleCancelOrder = async order => {
    const orderId = getOrderId(order);
    if (!orderId) return;

    try {
      setCancellingId(orderId);
      await cancelOrder(orderId);
      updateOrderLocally(orderId, {
        status: 'cancelled',
        rawStatus: 'cancelled',
        paymentStatus: normalizePaymentStatus(order),
      });
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to cancel order.');
    } finally {
      setCancellingId(null);
    }
  };

  const renderStatusBadge = order => {
    const rawStatus = order?.rawStatus || order?.status || 'pending';
    const key = normalizeStatusKey(rawStatus);
    const colors = STATUS_COLORS[key] || STATUS_COLORS.pending;
    const label = CUSTOMER_STATUS_MAP[key] || rawStatus;

    return (
      <span className={css(styles.statusBadge)} style={{backgroundColor: colors.bg, color: colors.text}}>
        {label}
      </span>
    );
  };

  const renderOrderCard = order => {
    const id = getOrderId(order);
    const statusKey = normalizeStatusKey(order?.rawStatus || order?.status);
    const canConfirm = statusKey === 'en_route' && (order?.riderConfirmedDelivery || order?.riderConfirmed) && !order?.customerConfirmedDelivery && !order?.customerConfirmed;
    const paymentPending = isPaymentPendingOrder(order);

    return (
      <article key={id} className={css(styles.orderCard)} onClick={() => setSelectedOrder(order)}>
        <div className={css(styles.cardTopRow)}>
          <div className={css(styles.restaurantIcon)}><FiCoffee /></div>
          <div className={css(styles.orderInfo)}>
            <h2>{getRestaurantName(order)}</h2>
            <p>{formatDateTime(order?.createdAt)}</p>
          </div>
          {renderStatusBadge(order)}
        </div>

        <div className={css(styles.metaRow)}>
          <span>Order #{id}</span>
          <span>{getOrderItemCount(order) || 'Items'} item(s)</span>
          <span>{order?.orderMode || order?.deliveryMode || order?.orderType || 'Order'}</span>
        </div>

        <div className={css(styles.cardBottomRow)}>
          <div>
            <small>{paymentPending ? 'Total to pay' : 'Total paid'}</small>
            <strong> R{formatMoney(calculateTotal(order))}</strong>
          </div>
          <button type="button" className={css(styles.viewDetailsButton)} onClick={event => {event.stopPropagation(); setSelectedOrder(order);}}>
            <FiEye /> View details
          </button>
        </div>

        {paymentPending ? (
          <div className={css(styles.paymentActions)}>
            <button
              type="button"
              className={css(styles.payButton)}
              onClick={event => {
                event.stopPropagation();
                handleCompletePayment(order);
              }}>
              <FiCreditCard /> Complete payment
            </button>
            <button
              type="button"
              className={css(styles.cancelOrderButton)}
              disabled={cancellingId === id}
              onClick={event => {
                event.stopPropagation();
                handleCancelOrder(order);
              }}>
              <FiXCircle /> {cancellingId === id ? 'Cancelling...' : 'Cancel order'}
            </button>
          </div>
        ) : null}

        {canConfirm ? (
          <button
            type="button"
            className={css(styles.confirmButton)}
            disabled={confirmingId === id}
            onClick={event => {
              event.stopPropagation();
              handleConfirmDelivery(order);
            }}>
            <FiCheckCircle /> {confirmingId === id ? 'Confirming...' : 'Confirm delivery'}
          </button>
        ) : null}
      </article>
    );
  };

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.shell)}>
        <section className={css(styles.header)}>
          <div>
            <p className={css(styles.kicker)}>YaBatho orders</p>
            <h1 className={css(styles.title)}>Order History</h1>
            <p className={css(styles.subtitle)}>Track live orders, payment state, and completed deliveries.</p>
          </div>

          <button type="button" className={css(styles.refreshButton)} onClick={fetchLatestOrders}>
            <FiRefreshCw /> Refresh
          </button>
        </section>

        {errorMessage ? <p className={css(styles.errorBox)}>{errorMessage}</p> : null}

        {loading ? (
          <section className={css(styles.stateCard)}>
            <div className={css(styles.spinner)} />
            <h2>Loading your orders...</h2>
          </section>
        ) : orders.length === 0 ? (
          <section className={css(styles.stateCard)}>
            <div className={css(styles.emptyIcon)}><FiFileText /></div>
            <h2>No orders yet</h2>
            <p>Once you place an order, your order history and live status will appear here.</p>
            <button type="button" onClick={() => navigate('/home')}>Explore outlets</button>
          </section>
        ) : (
          <>
            <section className={css(styles.ordersGrid)}>{visibleOrders.map(renderOrderCard)}</section>

            {visibleCount < orders.length ? (
              <button type="button" className={css(styles.viewMoreButton)} onClick={() => setVisibleCount(prev => prev + 6)}>
                View more orders <FiChevronDown />
              </button>
            ) : null}
          </>
        )}
      </main>

      {selectedOrder ? (
        <div className={css(styles.modalOverlay)} onClick={() => setSelectedOrder(null)}>
          <section className={css(styles.modal)} onClick={event => event.stopPropagation()}>
            <button type="button" className={css(styles.closeButton)} onClick={() => setSelectedOrder(null)}>×</button>
            <header className={css(styles.modalHeader)}>
              <div className={css(styles.modalHeaderIcon)}><FiFileText /></div>
              <div>
                <p className={css(styles.modalKicker)}>YaBatho order</p>
                <h2 className={css(styles.modalTitle)}>Order #{getOrderId(selectedOrder)}</h2>
              </div>
            </header>
            <div className={css(styles.statusCard)}>
              <div>
                <span className={css(styles.statusCardLabel)}>Current status</span>
                <strong className={css(styles.statusCardValue)}>{CUSTOMER_STATUS_MAP[normalizeStatusKey(selectedOrder?.rawStatus || selectedOrder?.status)] || titleCase(selectedOrder?.status || 'Pending')}</strong>
              </div>
              {renderStatusBadge(selectedOrder)}
            </div>
            <div className={css(styles.restaurantDetailCard)}>
              <div className={css(styles.restaurantDetailIcon)}><FiCoffee /></div>
              <div>
                <span className={css(styles.detailLabel)}>Restaurant</span>
                <strong className={css(styles.restaurantDetailName)}>{getRestaurantName(selectedOrder)}</strong>
              </div>
            </div>

            <div className={css(styles.detailRows)}>
              <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Date placed</span><strong className={css(styles.detailValue)}>{formatDateTime(selectedOrder?.createdAt)}</strong></div>
              <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Order type</span><strong className={css(styles.detailValue)}>{getOrderMode(selectedOrder)}</strong></div>
              {selectedOrder?.orderMode === 'Dining' ? (
                <>
                  <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Guests</span><strong className={css(styles.detailValue)}>{selectedOrder?.diningGuests || 1}</strong></div>
                  {selectedOrder?.reservationNotes ? (
                    <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Reservation notes</span><strong className={css(styles.detailValue)}>{selectedOrder.reservationNotes}</strong></div>
                  ) : null}
                </>
              ) : null}
              <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Payment status</span><strong className={css(styles.detailValue)}>{titleCase(selectedOrder?.paymentStatus || (selectedOrder?.paymentConfirmed ? 'paid' : 'pending'))}</strong></div>
              <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Payment method</span><strong className={css(styles.detailValue)}>{titleCase(selectedOrder?.paymentMethod || 'Not specified')}</strong></div>
              <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Delivery fee</span><strong className={css(styles.detailValue)}>R {formatMoney(selectedOrder?.deliveryFee)}</strong></div>
              <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Platform fee</span><strong className={css(styles.detailValue)}>R {formatMoney(selectedOrder?.platformFee)}</strong></div>
              {getOrderTips(selectedOrder).preDelivery > 0 ? (
                <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Tip</span><strong className={css(styles.detailValue)}>R {formatMoney(getOrderTips(selectedOrder).preDelivery)}</strong></div>
              ) : null}
              {getOrderTips(selectedOrder).postDelivery > 0 ? (
                <div className={css(styles.detailBox)}><span className={css(styles.detailLabel)}>Tip added after delivery</span><strong className={css(styles.detailValue)}>R {formatMoney(getOrderTips(selectedOrder).postDelivery)}</strong></div>
              ) : null}
            </div>
            {getOrderTips(selectedOrder).total > 0 ? (
              <div className={css(styles.tipSummary)}><span>Total tips</span><strong>R {formatMoney(getOrderTips(selectedOrder).total)}</strong></div>
            ) : null}
            <div className={css(styles.totalCard)}><span>Total</span><strong>R {formatMoney(calculateTotal(selectedOrder))}</strong></div>

            {isPaymentPendingOrder(selectedOrder) ? (
              <div className={css(styles.modalActions)}>
                <button type="button" className={css(styles.payButton)} onClick={() => handleCompletePayment(selectedOrder)}>
                  <FiCreditCard /> Complete payment
                </button>
                <button
                  type="button"
                  className={css(styles.cancelOrderButton)}
                  disabled={cancellingId === getOrderId(selectedOrder)}
                  onClick={() => handleCancelOrder(selectedOrder)}>
                  <FiXCircle /> {cancellingId === getOrderId(selectedOrder) ? 'Cancelling...' : 'Cancel order'}
                </button>
              </div>
            ) : null}

            {selectedOrder?.deliveryAddress ? (
              <div className={css(styles.addressBox)}>
                <div className={css(styles.addressIcon)}><FiMapPin /></div>
                <div><span className={css(styles.detailLabel)}>Delivery address</span><p className={css(styles.addressText)}>{selectedOrder.deliveryAddress}</p></div>
              </div>
            ) : null}

            {getOrderMode(selectedOrder) === 'Delivery' ? (() => {
              const rider = getRiderDetails(selectedOrder, orderTracking);
              return (
                <div className={css(styles.riderCard)}>
                  <div className={css(styles.sectionHeading)}>
                    <div><p className={css(styles.modalKicker)}>Delivery partner</p><h3 className={css(styles.sectionTitle)}>Rider information</h3></div>
                    <FiTruck />
                  </div>
                  <div className={css(styles.riderIdentity)}>
                    <div className={css(styles.riderAvatar)}><FiUser /></div>
                    <div>
                      <strong className={css(styles.riderName)}>{detailsLoading ? 'Loading rider details...' : rider.name}</strong>
                      <span className={css(styles.riderMeta)}>{rider.assigned ? titleCase(rider.vehicleType || 'Vehicle not added') : 'Details will appear once a rider is assigned.'}</span>
                    </div>
                  </div>
                  {rider.assigned ? (
                    <div className={css(styles.riderGrid)}>
                      <div className={css(styles.riderDetail)}><FiTruck /><span><small className={css(styles.riderDetailLabel)}>Number plate</small><strong className={css(styles.riderDetailValue)}>{rider.numberPlate || 'Not added'}</strong></span></div>
                      <div className={css(styles.riderDetail)}><FiPhone /><span><small className={css(styles.riderDetailLabel)}>Phone number</small><strong className={css(styles.riderDetailValue)}>{rider.phone || 'Not added'}</strong></span></div>
                    </div>
                  ) : null}
                </div>
              );
            })() : null}

            <div className={css(styles.itemsBox)}>
              <div className={css(styles.sectionHeading)}><div><p className={css(styles.modalKicker)}>Your order</p><h3 className={css(styles.sectionTitle)}>Items</h3></div><FiShoppingBag /></div>
              {getOrderItems(selectedOrder).length ? (
                getOrderItems(selectedOrder).map((item, index) => (
                  <div key={`${getOrderId(selectedOrder)}_${index}`} className={css(styles.itemRow)}>
                    <span>{item?.quantity || 1} × {item?.name || item?.menuItem?.name || 'Item'}</span>
                    <strong>
                      {item?.priceAtOrder || item?.price || item?.menuItem?.price ? `R ${formatMoney(item?.priceAtOrder || item?.price || item?.menuItem?.price)}` : ''}
                    </strong>
                  </div>
                ))
              ) : (
                <p className={css(styles.noItemsText)}>Item details unavailable.</p>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
    padding: '24px 24px 112px',
    boxSizing: 'border-box',
    '@media (max-width: 720px)': {padding: '16px 14px 104px'},
  },
  shell: {width: '100%', maxWidth: '1120px', margin: '0 auto'},
  header: {
    backgroundColor: '#0F3D2E',
    border: '1px solid rgba(201, 161, 74, 0.45)',
    borderRadius: '34px',
    padding: '30px',
    boxShadow: '0 22px 50px rgba(15, 61, 46, 0.16)',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '24px',
    marginBottom: '22px',
    '@media (max-width: 720px)': {borderRadius: '28px', padding: '22px', flexDirection: 'column'},
  },
  kicker: {
    margin: '0 0 8px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  title: {margin: 0, color: '#ffffff', fontSize: '42px', lineHeight: 1.04, letterSpacing: '-1.8px', fontWeight: '900'},
  subtitle: {maxWidth: '620px', margin: '14px 0 0', color: '#F4EBD8', fontSize: '15px', lineHeight: 1.75},
  refreshButton: {
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '13px 18px',
    fontWeight: '900',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    minHeight: '46px',
  },
  errorBox: {backgroundColor: '#FFE9E9', border: '1px solid #F5B8B8', color: '#D9534F', borderRadius: '18px', padding: '12px 14px', fontWeight: '800'},
  stateCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '40px 20px',
    textAlign: 'center',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
  },
  emptyIcon: {fontSize: '40px', marginBottom: '12px'},
  spinner: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    border: '4px solid #F4EBD8',
    borderTopColor: '#C9A14A',
    margin: '0 auto 16px',
  },
  ordersGrid: {display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', '@media (max-width: 860px)': {gridTemplateColumns: '1fr'}},
  orderCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '28px',
    padding: '20px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',

    ':hover': {
      transform: 'translateY(-2px)',
      borderColor: '#C9A14A',
      boxShadow: '0 18px 42px rgba(15, 61, 46, 0.11)',
    },

    '@media (max-width: 520px)': {
      padding: '16px',
      borderRadius: '24px',
    },
  },
  cardTopRow: {
    display: 'grid',
    gridTemplateColumns: '46px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: '12px',

    '@media (max-width: 420px)': {
      gridTemplateColumns: '46px minmax(0, 1fr)',
    },
  },
  restaurantIcon: {width: '46px', height: '46px', borderRadius: '50%', backgroundColor: '#0F3D2E', color: '#C9A14A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0},
  orderInfo: {flex: 1, minWidth: 0, paddingRight: '8px', h2: {margin: 0, color: '#0F3D2E', fontSize: '18px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}, p: {margin: '4px 0 0', color: '#8C8170', fontSize: '12px'}},
  statusBadge: {display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '999px', padding: '8px 11px', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', flexShrink: 0, maxWidth: '150px', textAlign: 'center', '@media (max-width: 420px)': {gridColumn: '1 / -1', justifySelf: 'start'}},
  metaRow: {display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '14px', span: {backgroundColor: '#F4EBD8', border: '1px solid #E6D8BE', color: '#0F3D2E', borderRadius: '999px', padding: '7px 10px', fontSize: '11px', fontWeight: '800'}},
  cardBottomRow: {
    marginTop: '14px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #F1E7D2',
    borderRadius: '22px',
    padding: '13px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',

    '@media (max-width: 420px)': {
      alignItems: 'stretch',
      flexDirection: 'column',
    },

    small: {
      display: 'block',
      color: '#8C8170',
      fontSize: '11px',
      lineHeight: 1.4,
    },

    strong: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '22px',
      fontWeight: '900',
      whiteSpace: 'nowrap',
    },

  },
  viewDetailsButton: {
    minHeight: '42px',
    border: '1px solid #0F3D2E',
    backgroundColor: '#ffffff',
    color: '#0F3D2E',
    borderRadius: '14px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: '900',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',

    ':hover': {
      backgroundColor: '#0F3D2E',
      color: '#ffffff',
    },

    '@media (max-width: 420px)': {
      width: '100%',
    },
  },
  paymentActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '12px',

    '@media (max-width: 420px)': {
      gridTemplateColumns: '1fr',
    },
  },
  payButton: {
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '12px 13px',
    fontWeight: '900',
    cursor: 'pointer',
    textAlign: 'center',
    minHeight: '48px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  cancelOrderButton: {
    border: '1px solid #F5B8B8',
    backgroundColor: '#FFE9E9',
    color: '#B63B31',
    borderRadius: '18px',
    padding: '12px 13px',
    fontWeight: '900',
    cursor: 'pointer',
    textAlign: 'center',
    minHeight: '48px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',

    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmButton: {width: '100%', marginTop: '12px', border: 'none', backgroundColor: '#C9A14A', color: '#0F3D2E', borderRadius: '18px', padding: '13px', fontWeight: '900', cursor: 'pointer', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'},
  viewMoreButton: {margin: '20px auto 0', border: '1px solid #E6D8BE', backgroundColor: '#ffffff', color: '#0F3D2E', borderRadius: '18px', padding: '13px 18px', fontWeight: '900', cursor: 'pointer', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'},
  modalOverlay: {position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 61, 46, 0.38)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px'},
  modal: {width: '100%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', backgroundColor: '#FFFDF7', border: '1px solid #E6D8BE', borderRadius: '30px', padding: '22px', position: 'relative', boxShadow: '0 28px 80px rgba(15, 61, 46, 0.24)'},
  modalHeader: {display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '44px', marginBottom: '16px'},
  modalHeaderIcon: {width: '48px', height: '48px', flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: '#0F3D2E', color: '#C9A14A', fontSize: '21px'},
  modalKicker: {margin: '0 0 3px', color: '#C9A14A', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.9px'},
  modalTitle: {margin: 0, color: '#0F3D2E', fontSize: '23px', lineHeight: 1.2, fontWeight: '900'},
  closeButton: {position: 'absolute', right: '16px', top: '16px', width: '34px', height: '34px', borderRadius: '50%', border: '1px solid #E6D8BE', backgroundColor: '#FFFDF7', color: '#0F3D2E', fontSize: '22px', cursor: 'pointer'},
  modalStatusRow: {marginBottom: '14px'},
  statusCard: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', padding: '16px', marginBottom: '12px', borderRadius: '22px', backgroundColor: '#0F3D2E'},
  statusCardLabel: {display: 'block', color: '#F4EBD8', fontSize: '11px', marginBottom: '4px'},
  statusCardValue: {display: 'block', color: '#ffffff', fontSize: '18px', lineHeight: 1.25},
  restaurantDetailCard: {display: 'flex', alignItems: 'center', gap: '12px', padding: '13px', marginBottom: '12px', border: '1px solid #E6D8BE', borderRadius: '20px', backgroundColor: '#F4EBD8'},
  restaurantDetailIcon: {width: '44px', height: '44px', flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: '#0F3D2E', color: '#C9A14A', fontSize: '20px'},
  restaurantDetailName: {display: 'block', marginTop: '3px', color: '#0F3D2E', fontSize: '17px', lineHeight: 1.35},
  modalActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '12px',

    '@media (max-width: 420px)': {
      gridTemplateColumns: '1fr',
    },
  },
  detailRows: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px',

    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',
    },

    div: {
      minWidth: 0,
      backgroundColor: '#FFFDF7',
      border: '1px solid #F1E7D2',
      borderRadius: '17px',
      padding: '12px',
    },

    span: {
      display: 'block',
      color: '#8C8170',
      fontSize: '11px',
      marginBottom: '6px',
      lineHeight: 1.35,
    },

    strong: {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '14px',
      lineHeight: 1.45,
      overflowWrap: 'anywhere',
    },
  },
  detailBox: {minWidth: 0, padding: '13px', border: '1px solid #F1E7D2', borderRadius: '17px', backgroundColor: '#ffffff'},
  detailLabel: {display: 'block', color: '#8C8170', fontSize: '11px', lineHeight: 1.35},
  detailValue: {display: 'block', marginTop: '5px', color: '#0F3D2E', fontSize: '14px', lineHeight: 1.45, overflowWrap: 'anywhere'},
  tipSummary: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '12px', padding: '12px 15px', border: '1px solid #E6D8BE', borderRadius: '16px', backgroundColor: '#F4EBD8', color: '#0F3D2E', fontSize: '13px', fontWeight: '800'},
  totalCard: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '15px 16px', borderRadius: '18px', backgroundColor: '#C9A14A', color: '#0F3D2E', fontWeight: '900', strong: {fontSize: '18px'}},
  addressBox: {display: 'flex', alignItems: 'flex-start', gap: '11px', marginTop: '12px', backgroundColor: '#F4EBD8', border: '1px solid #E6D8BE', borderRadius: '18px', padding: '13px'},
  addressIcon: {width: '38px', height: '38px', flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: '#0F3D2E', color: '#C9A14A'},
  addressText: {margin: '4px 0 0', color: '#0F3D2E', lineHeight: 1.5, fontWeight: '800'},
  riderCard: {marginTop: '14px', padding: '15px 16px', border: '1px solid #E6D8BE', borderRadius: '22px', backgroundColor: '#ffffff'},
  sectionHeading: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', color: '#C9A14A'},
  sectionTitle: {margin: 0, color: '#0F3D2E', fontSize: '18px', lineHeight: 1.3, fontWeight: '900'},
  riderIdentity: {display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px'},
  riderName: {display: 'block', color: '#0F3D2E', fontSize: '16px', lineHeight: 1.35},
  riderMeta: {display: 'block', marginTop: '3px', color: '#746A5B', fontSize: '12px', lineHeight: 1.4},
  riderAvatar: {width: '46px', height: '46px', flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', backgroundColor: '#0F3D2E', color: '#C9A14A', fontSize: '20px'},
  riderGrid: {display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '9px', marginTop: '12px', '@media (max-width: 480px)': {gridTemplateColumns: '1fr'}},
  riderDetail: {display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, padding: '10px 12px', borderRadius: '15px', backgroundColor: '#FFFDF7', color: '#C9A14A'},
  riderDetailLabel: {display: 'block', color: '#8C8170', fontSize: '10px', lineHeight: 1.3},
  riderDetailValue: {display: 'block', marginTop: '3px', color: '#0F3D2E', fontSize: '13px', lineHeight: 1.35, overflowWrap: 'anywhere'},
  itemsBox: {marginTop: '14px', padding: '15px 16px', border: '1px solid #E6D8BE', borderRadius: '22px', backgroundColor: '#ffffff'},
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    borderBottom: '1px solid #F1E7D2',
    padding: '10px 0',
    color: '#0F3D2E',
    alignItems: 'flex-start',

    span: {
      minWidth: 0,
      lineHeight: 1.45,
    },

    strong: {
      whiteSpace: 'nowrap',
    },
  },
  noItemsText: {color: '#8C8170', margin: 0},
});
